import {
  type TrackPerfAction,
  trackPerfEvent,
} from '../../services/analytics/perfEvents';
import {datadogEnabled} from './datadog';
import {getDatadogRum} from './datadogRum';
import {getPerfMetricSession, getSessionElapsed} from './pageMetricSession';

const ALWAYS_SEND_TO_SEGMENT_ACTIONS: ReadonlyArray<TrackPerfAction> = [
  'customer_ready',
];

export type MetricsContext = Record<string, unknown>;

/**
 * Extends MetricsContext to require a `pageName` — the fine-grained view identifier
 * (e.g. 'RegistriesDiscoverCollections') used for RUM custom metrics and Segment dashboards.
 */
export type PerformanceMetricsContext = MetricsContext & {pageName: string};

/**
 * Sends a page-level metric to the appropriate backend:
 * - `customer_ready`: always Segment + Datadog (when enabled)
 * - `data_fetched`: Datadog when enabled, Segment only when Datadog is unavailable
 *
 * @param metricsContext - Additional metadata context for the event (must include pageName)
 * @param action - What happened (e.g., 'customer_ready', 'data_fetched')
 * @param data - Event-specific payload
 */
export function trackPageMetric(
  metricsContext: MetricsContext = {},
  action: TrackPerfAction,
  data: Record<string, unknown> = {}
): void {
  const session = getPerfMetricSession();
  const pageGroup = session?.pageGroup ?? 'Unknown Route';
  const sessionData: Record<string, unknown> = session
    ? {
        uid: session.uid,
        elapsed: Math.round(getSessionElapsed()!),
        ...session.context,
      }
    : {};

  const eventData = {action, ...sessionData, ...metricsContext, ...data};
  const datadogAvailable = datadogEnabled();
  const shouldSendToSegment =
    ALWAYS_SEND_TO_SEGMENT_ACTIONS.includes(action) || !datadogAvailable;

  if (shouldSendToSegment) {
    trackPerfEvent({...eventData, pageName: pageGroup});
  }
  getDatadogRum().addAction(`${pageGroup}.${action}`, eventData);
}
